import { admin_apiConfig } from 'shared/config/config';
import { authFetch } from 'shared/api/authFetch';
import { normalizeTimelineBlock, toRenderableText } from './chatHistoryNormalization';

const TEXT_SUMMARY_MAX = 400;
const TOOL_IO_SUMMARY_MAX = 900;

export const truncateText = (text, maxChars = TEXT_SUMMARY_MAX) => {
  const value = String(text || '').trim();
  if (!value) return '';
  if (value.length <= maxChars) return value;
  return `${value.slice(0, maxChars)}… [${value.length - maxChars} more chars]`;
};

export const truncateJson = (value, maxChars = TOOL_IO_SUMMARY_MAX) => {
  let serialized;
  try {
    serialized = JSON.stringify(value);
  } catch (err) {
    serialized = String(value);
  }
  return truncateText(serialized, maxChars);
};

const makeBlockId = () => `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const getEventBlocks = (event) => {
  if (!event) return [];
  if (Array.isArray(event.blocks)) return event.blocks;
  return event.content && Array.isArray(event.content.blocks) ? event.content.blocks : [];
};

const timelineEventToMessage = (event) => {
  const rawBlocks = getEventBlocks(event);
  const blocks = Array.isArray(rawBlocks)
    ? rawBlocks.map((block) => normalizeTimelineBlock(block, makeBlockId)).filter(Boolean)
    : [];
  const blockTextContent = blocks
    .filter((b) => b.type === 'text')
    .map((b) => toRenderableText(b.text))
    .join('\n')
    .trim();
  return {
    id: event.id || `event-${event.seq}`,
    role: 'user',
    content: blockTextContent || toRenderableText(event?.content),
    blocks,
    timestamp: event.timestamp || null,
  };
};

export const timelineToExportMessages = (timeline) => {
  if (!Array.isArray(timeline) || timeline.length === 0) return [];
  const messages = [];
  let assistantAccumulator = null;

  const flushAssistant = () => {
    if (!assistantAccumulator) return;
    const { blocks, timestamp, firstEvent } = assistantAccumulator;
    const content =
      blocks
        .filter((b) => b.type === 'text')
        .map((b) => toRenderableText(b.text))
        .join('\n')
        .trim() || toRenderableText(firstEvent?.content);
    messages.push({
      id: firstEvent.id || `event-${firstEvent.seq}`,
      role: 'assistant',
      content,
      blocks,
      timestamp: timestamp || null,
      metadata: { run_id: firstEvent.run_id || null },
    });
    assistantAccumulator = null;
  };

  for (const event of timeline) {
    const actorType = event.actor_type || event.actorType;
    if (actorType === 'system') continue;

    if (actorType === 'user') {
      flushAssistant();
      messages.push(timelineEventToMessage(event));
      continue;
    }

    const rawBlocks = getEventBlocks(event);
    const blocks = Array.isArray(rawBlocks)
      ? rawBlocks.map((block) => normalizeTimelineBlock(block, makeBlockId)).filter(Boolean)
      : [];
    if (blocks.length === 0 && actorType === 'tool') continue;

    if (!assistantAccumulator) {
      assistantAccumulator = {
        blocks: [...blocks],
        seenToolCallIds: new Set(),
        timestamp: event.timestamp || null,
        firstEvent: event,
        runId: event.run_id || null,
      };
      for (const b of assistantAccumulator.blocks) {
        if (b?.type === 'tool_call' && b.toolCallId) {
          assistantAccumulator.seenToolCallIds.add(String(b.toolCallId));
        }
      }
    } else {
      if ((event.run_id || null) !== assistantAccumulator.runId) {
        flushAssistant();
        assistantAccumulator = {
          blocks: [...blocks],
          seenToolCallIds: new Set(),
          timestamp: event.timestamp || null,
          firstEvent: event,
          runId: event.run_id || null,
        };
        for (const b of assistantAccumulator.blocks) {
          if (b?.type === 'tool_call' && b.toolCallId) {
            assistantAccumulator.seenToolCallIds.add(String(b.toolCallId));
          }
        }
        continue;
      }
      for (const b of blocks) {
        if (b?.type === 'tool_call' && b.toolCallId) {
          const key = String(b.toolCallId);
          if (assistantAccumulator.seenToolCallIds.has(key)) continue;
          assistantAccumulator.seenToolCallIds.add(key);
        }
        if (b?.type === 'tool_result' && b.toolCallId) {
          const key = String(b.toolCallId);
          const idx = assistantAccumulator.blocks.findIndex(
            (existing) => existing?.type === 'tool_call' && String(existing.toolCallId) === key
          );
          if (idx >= 0) {
            assistantAccumulator.blocks[idx] = {
              ...assistantAccumulator.blocks[idx],
              result: b.result,
              status: b.isError ? 'failed' : 'completed',
            };
            continue;
          }
        }
        assistantAccumulator.blocks.push(b);
      }
    }
  }

  flushAssistant();
  return messages;
};

const SUMMARY_KEYS = [
  'success',
  'valid',
  'status',
  'error',
  'error_type',
  'message',
  'truncated',
  'count',
  'total',
  'phase',
  'summary',
  'output_summary',
];

export const summarizeStructuredValue = (value, maxChars = TOOL_IO_SUMMARY_MAX) => {
  if (value == null) return '(none)';
  if (typeof value === 'string') return truncateText(value, maxChars);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    if (value.every((item) => ['string', 'number', 'boolean'].includes(typeof item))) {
      return truncateText(value.join(', '), maxChars);
    }
    return truncateJson(value, maxChars);
  }
  if (typeof value !== 'object') return truncateText(String(value), maxChars);

  const summaryParts = [];
  for (const key of SUMMARY_KEYS) {
    if (value[key] != null && value[key] !== '') {
      summaryParts.push(`${key}=${summarizeStructuredValue(value[key], 120)}`);
    }
  }
  if (Array.isArray(value.errors) && value.errors.length > 0) {
    summaryParts.push(`errors=${value.errors.length}`);
  }
  if (Array.isArray(value.issues) && value.issues.length > 0) {
    summaryParts.push(`issues=${value.issues.length}`);
  }
  if (summaryParts.length > 0) {
    return truncateText(summaryParts.join(' · '), maxChars);
  }

  const flat = Object.entries(value)
    .slice(0, 6)
    .map(([key, nested]) => {
      if (nested == null) return `${key}=null`;
      if (typeof nested === 'object') return `${key}={…}`;
      return `${key}=${truncateText(String(nested), 80)}`;
    })
    .join(' · ');
  return truncateText(flat || truncateJson(value, maxChars), maxChars);
};

const renderToolSection = (block) => {
  const toolName = block.toolName || block.tool_name || 'tool';
  const lines = [`#### Tool: ${toolName}`, ''];
  if (block.reasoning) {
    lines.push('**Reasoning:**', truncateText(block.reasoning, TEXT_SUMMARY_MAX), '');
  }
  lines.push('**Input:**', summarizeStructuredValue(block.args ?? block.input ?? {}), '');
  if (block.result != null) {
    lines.push('**Output:**', summarizeStructuredValue(block.result), '');
  } else if (block.status === 'running') {
    lines.push('**Output:** _(in progress)_', '');
  } else {
    lines.push('**Output:** _(none)_', '');
  }
  return lines;
};

const renderAssistantBlock = (block) => {
  if (!block) return [];
  const type = String(block.type || '').toLowerCase();

  if (type === 'thinking') {
    const text = toRenderableText(block.text);
    if (!text.trim()) return [];
    return ['#### Thinking', '', text.trim(), ''];
  }
  if (type === 'text') {
    const text = toRenderableText(block.text);
    if (!text.trim()) return [];
    return [text.trim(), ''];
  }
  if (type === 'tool_call') {
    return renderToolSection(block);
  }
  if (type === 'notice') {
    const text = toRenderableText(block.text);
    if (!text.trim()) return [];
    return [`> _Notice:_ ${truncateText(text, TEXT_SUMMARY_MAX)}`, ''];
  }
  if (type === 'attachment') {
    return [`_Attachment: ${block.filename || 'file'}_`, ''];
  }
  if (type === 'sub_agent') {
    const lines = [
      `#### Sub-agent: ${block.agentName || block.agentIdentifier || 'sub_agent'}`,
      '',
    ];
    if (block.error) lines.push(`**Error:** ${truncateText(block.error, TEXT_SUMMARY_MAX)}`, '');
    for (const inner of block.blocks || []) {
      lines.push(...renderAssistantBlock(inner));
    }
    return lines;
  }
  return [];
};

const renderUserMessage = (message) => {
  const text =
    (Array.isArray(message.blocks)
      ? message.blocks
          .filter((b) => b?.type === 'text')
          .map((b) => toRenderableText(b.text))
          .join('\n')
          .trim()
      : '') || String(message.content || '').trim();
  if (!text) return [];
  return ['## User', '', text, ''];
};

const renderAssistantMessage = (message) => {
  const blocks = Array.isArray(message.blocks) ? message.blocks.filter(Boolean) : [];
  const renderedBlocks = blocks.flatMap((block) => renderAssistantBlock(block));
  const fallbackText = String(message.content || '').trim();
  const hasRenderableBlocks = renderedBlocks.some((line) => line && !line.startsWith('####'));

  if (renderedBlocks.length === 0 && !fallbackText) return [];

  const lines = ['## Assistant', ''];
  if (renderedBlocks.length > 0) {
    lines.push(...renderedBlocks);
  } else if (fallbackText) {
    lines.push(fallbackText, '');
  } else if (!hasRenderableBlocks && fallbackText) {
    lines.push('#### Response', '', fallbackText, '');
  }
  return lines;
};

export const renderConversationTurn = (message) => {
  if (!message) return [];
  if (message.role === 'user') return renderUserMessage(message);
  if (message.role === 'assistant') return renderAssistantMessage(message);
  return [];
};

export const resolveExportMessages = ({ messages = [], timeline = [], isStreaming = false } = {}) => {
  const uiMessages = Array.isArray(messages) ? messages.filter(Boolean) : [];
  const timelineMessages = timelineToExportMessages(timeline);
  if (uiMessages.length === 0) return timelineMessages;
  if (timelineMessages.length === 0 || isStreaming || uiMessages.length >= timelineMessages.length) {
    return uiMessages;
  }
  return timelineMessages;
};

export const buildConversationTraceMarkdown = ({
  messages = [],
  timeline = [],
  metadata = {},
}) => {
  const exportMessages = resolveExportMessages({
    messages,
    timeline,
    isStreaming: metadata?.isStreaming,
  });

  const lines = [
    '# Conversation Export',
    '',
    '> Conversation transcript for debugging the chat agent (`backend/services/chat_agents/`).',
    '',
  ];

  const metaParts = [];
  if (metadata?.sessionId) metaParts.push(`session \`${metadata.sessionId}\``);
  if (metadata?.agentDisplayName) metaParts.push(metadata.agentDisplayName);
  if (metadata?.exportedAt) metaParts.push(`exported ${metadata.exportedAt}`);
  if (metadata?.isStreaming) metaParts.push('includes in-progress turn');
  if (metaParts.length > 0) {
    lines.push(metaParts.join(' · '), '', '---', '');
  }

  if (exportMessages.length === 0) {
    lines.push('_No conversation content to export yet._', '');
    return lines.join('\n');
  }

  exportMessages.forEach((message) => {
    const turn = renderConversationTurn(message);
    if (turn.length > 0) {
      lines.push(...turn);
      lines.push('---', '');
    }
  });

  return lines.join('\n').trimEnd() + '\n';
};

export const fetchSessionTimelineForExport = async ({
  sessionId,
  userProfileId,
  signal,
}) => {
  const url = new URL(
    `${admin_apiConfig.baseURL}${admin_apiConfig.endpoints.agentSessionTimeline}/${sessionId}/timeline`
  );
  url.searchParams.set('user_profile_id', String(userProfileId));
  url.searchParams.set('include_provider_responses', 'false');
  const response = await authFetch(url.toString(), { method: 'GET', signal });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to load session timeline for export');
  }
  return response.json();
};

export const buildExportFilename = (sessionId) => {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const shortSessionId = String(sessionId || 'session').slice(0, 8);
  return `conversation-${shortSessionId}-${stamp}.md`;
};

export const downloadMarkdownFile = (content, filename) => {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.URL.revokeObjectURL(url);
};

export const exportConversationTraceToFile = async ({
  sessionId,
  userProfileId,
  messages = [],
  metadata = {},
  signal,
}) => {
  let timeline = [];
  if (sessionId && userProfileId && messages.length === 0) {
    const timelinePayload = await fetchSessionTimelineForExport({
      sessionId,
      userProfileId,
      signal,
    });
    timeline = Array.isArray(timelinePayload?.timeline) ? timelinePayload.timeline : [];
  }

  const exportMessages = resolveExportMessages({
    messages,
    timeline,
    isStreaming: metadata?.isStreaming,
  });

  const markdown = buildConversationTraceMarkdown({
    messages,
    timeline,
    metadata: {
      ...metadata,
      sessionId: sessionId || metadata?.sessionId,
      exportedAt: metadata?.exportedAt || new Date().toISOString(),
    },
  });
  const filename = buildExportFilename(sessionId || metadata?.sessionId);
  downloadMarkdownFile(markdown, filename);
  return { filename, turnCount: exportMessages.length };
};
